import {
    getAuthorityEndDate,
    getClientNames,
    type InternalPurchaserSchema,
    type PurchaserListFilters,
    type PurchaserStatus,
} from '@buyerflo/core/schemas/purchaser'
import type { Permission } from '@buyerflo/core/schemas/roles'
import type { BadgeTone } from './color'

// Design palette (BuyerFlo 5-stage taxonomy) mapped to semantic Badge tones, so
// status colours resolve through the shared theme tokens (statusInfo/Warn/Violet/
// Ok/Danger + their soft tints) instead of duplicating their hex values.
export const PurchaserStatusTones: Record<PurchaserStatus, BadgeTone> = {
    consultation: 'info',
    searching: 'warn',
    'under offer': 'violet',
    settled: 'ok',
    lost: 'danger',
}

export const getPurchaserStatusOptions = (
    purchaser: { purchaserStatus?: PurchaserStatus },
    permissions: Permission[],
): { value: PurchaserStatus; disabled?: boolean }[] => {
    const hasFullControl = permissions.includes('purchasers.fullControl')

    return [
        {
            disabled: purchaser.purchaserStatus !== 'consultation' && !hasFullControl,
            value: 'consultation',
        },
        {
            value: 'searching',
        },
        {
            disabled: !hasFullControl,
            value: 'under offer',
        },
        {
            disabled: !hasFullControl,
            value: 'settled',
        },
        {
            disabled: !hasFullControl,
            value: 'lost',
        },
    ]
}

/**
 * Resolves an agent id to a display name — pass `useUserName()` (web) or any
 * equivalent lookup. Agent names are only known client-side, so search and the
 * agent sort both take one of these.
 */
export type AgentNameLookup = (userId: string) => string

const agentNames = (purchaser: InternalPurchaserSchema, lookup: AgentNameLookup) =>
    (purchaser.agents ?? []).map(agent => lookup(agent.userId)).join(' ')

/** The region shown in the clients list — the wishlist's target suburbs. */
export const getPurchaserRegion = (purchaser: InternalPurchaserSchema) =>
    purchaser.wishlist?.targetSuburbs ?? ''

/** The budget shown in the clients list — the wishlist's budget range. */
export const getPurchaserBudget = (purchaser: InternalPurchaserSchema) =>
    purchaser.wishlist?.budgetRange ?? ''

/**
 * Free-text search over the fields the clients list actually shows: client
 * names, status, agent names, property address and target suburbs. Case- and
 * position-insensitive substring match (no fuzzy matching or typo tolerance —
 * this replaced a Typesense index).
 */
export const purchaserMatchesSearch = (
    purchaser: InternalPurchaserSchema,
    search: string,
    lookup: AgentNameLookup,
) => {
    const needle = search.trim().toLowerCase()
    if (needle === '') return true

    return [
        getClientNames(purchaser.buyers),
        purchaser.purchaserStatus ?? '',
        purchaser.propertyAddress ?? '',
        getPurchaserRegion(purchaser),
        agentNames(purchaser, lookup),
    ]
        .join(' ')
        .toLowerCase()
        .includes(needle)
}

/**
 * Sort key → comparable value. Keys match the column `sortString`s used by the
 * clients table; an unknown key sorts everything equal (i.e. leaves the order
 * alone).
 */
export const purchaserSortValue = (
    purchaser: InternalPurchaserSchema,
    sort: string,
    lookup: AgentNameLookup,
): string | number => {
    switch (sort) {
        case 'buyerNames':
            return getClientNames(purchaser.buyers).toLowerCase()
        case 'purchaserStatus':
            return purchaser.purchaserStatus ?? ''
        case 'userNames':
            return agentNames(purchaser, lookup).toLowerCase()
        case 'budgetRange':
            return getPurchaserBudget(purchaser).toLowerCase()
        case 'region':
            return getPurchaserRegion(purchaser).toLowerCase()
        case 'authorityEndDate':
            return getAuthorityEndDate(purchaser)?.getTime() ?? 0
        // 'addressString' is the legacy name persisted in session storage from when
        // the Client column sorted on the property address.
        case 'addressString':
        case 'propertyAddress':
            return (purchaser.propertyAddress ?? '').toLowerCase()
        default:
            return ''
    }
}

/**
 * Narrow and order the full client list in the browser.
 *
 * An empty `status` / `agentIds` list means "don't filter on it" (that is how the
 * status chips and agent picker have always behaved — deselecting everything
 * shows everything). `actionFilters` match the value stored on the purchaser, so
 * a purchaser that has never had the action set is excluded even though the table
 * renders the config's first value as its default.
 */
export const filterAndSortPurchasers = (
    purchasers: InternalPurchaserSchema[],
    filters: PurchaserListFilters,
    lookup: AgentNameLookup,
): InternalPurchaserSchema[] => {
    const { search, status, agentIds, actionFilters, sort, sortDirection } = filters

    const filtered = purchasers.filter(purchaser => {
        if (status.length > 0 && !status.includes(purchaser.purchaserStatus)) return false

        if (
            agentIds.length > 0 &&
            !(purchaser.agents ?? []).some(agent => agentIds.includes(agent.userId))
        ) {
            return false
        }

        const actionsMatch = Object.entries(actionFilters).every(([key, values]) => {
            if (values.length === 0) return true
            const value = purchaser.additionalDetails?.[key]
            return value != null && values.includes(value)
        })
        if (!actionsMatch) return false

        return purchaserMatchesSearch(purchaser, search, lookup)
    })

    if (!sort) return filtered

    const direction = sortDirection === 'asc' ? 1 : -1

    return [...filtered].sort((a, b) => {
        const left = purchaserSortValue(a, sort, lookup)
        const right = purchaserSortValue(b, sort, lookup)

        if (typeof left === 'number' && typeof right === 'number') {
            return (left - right) * direction
        }

        return String(left).localeCompare(String(right)) * direction
    })
}
